﻿import React, { Fragment, useState, useEffect } from 'react';
import { FormRadio, FormDateInput, FormTextInput, FormCheckboxList } from "components/form-fields";
import { ShowHide } from "components/layout";
import { WorkingTimeSettings } from 'components/settings';

import { UserAccess } from 'components/user-access';
import { useValidation } from 'hooks/useValidation';
import PropTypes from 'prop-types';
import { HelperFunctions } from 'helpers';
import moment from "moment";
import { Intent } from '@blueprintjs/core';
import { NotificationToaster } from "components/notifications";
import _ from 'underscore';


export function LocationSettings(
    {
        saving = false,
        editing = false,
        canRemoveBusinessArea,
        generalSettings,
        loading,
        onSettingsUpdate,
        onValidationUpdate,
        user
    }
) {


    const [generalSettingsValid, generalSettingsErrors, validateGeneralSettings] = useValidation(); 
    const [tachoSettingsValid, tachoSettingsErrors, validateTachoSettings] = useValidation(); 
    const [businessAreas, setBusinessAreas] = useState([]);
    const [workingTimeValid, setWorkingTimeValid] = useState(true);
    const [loadingValue, setLoading] = useState(true);

    const defaultOptions = {
        inheritSettings: [{ value: true, label: "Inherit default settings" }, { value: false, label: "Apply custom settings" }]
    };

    useEffect(() => {
        setLoading(loading);
    }, [loading]);


    useEffect(() => {
        setBusinessAreas(generalSettings.businessAreas)
    }, [generalSettings]);

    useEffect(() => {
        onValidationUpdate(generalSettingsValid && tachoSettingsValid && (generalSettings.tachoSettings.useTachoAnalysisDefaults || workingTimeValid));
    }, [generalSettingsValid, generalSettings.tachoSettings.useTachoAnalysisDefaults, tachoSettingsValid, workingTimeValid]);

    useEffect(validateSettings, [generalSettings]);

    function validateSettings() {
        let generalRules = [{ fieldName: "name", required: true }];

        if (user.isInternal) {
            generalRules.push({ fieldName: "businessAreas", required: true });
        }

        validateGeneralSettings(generalRules, generalSettings.generalSettings);

        let tachoRules = [];
        if (!generalSettings.tachoSettings.useTachoAnalysisDefaults) {
            tachoRules.push({ fieldName: "tachoAnalysisStartDate", required: true });
        }
        validateTachoSettings(tachoRules, generalSettings.tachoSettings);
    }

    function onNameChange(event) {
        var updateSettings = HelperFunctions.deepClone(generalSettings);
        updateSettings.generalSettings.name = event.target.value;
        onSettingsUpdate(updateSettings);
    }

    function onReferenceChange(event) {
        var updateSettings = HelperFunctions.deepClone(generalSettings);
        updateSettings.generalSettings.reference = event.target.value;
        onSettingsUpdate(updateSettings);
    }

    function handleCheckboxChange(event, id) {

        var tempBusinessAreas = [...businessAreas];

        var match = _.find(tempBusinessAreas, function (item) {
            return item.id === id;
        });

        if (!event.target.checked && canRemoveBusinessArea) {
            if (!canRemoveBusinessArea(match.name)) {
                    NotificationToaster.show(Intent.WARNING, 'You have contacts assigned to this business area. Please remove them before you can unselect this business area.');
                return;
            }
        }

        if (match != null) {
            match.checked = event.target.checked;
        }

        var selectedItems = _.chain(tempBusinessAreas).filter(function (item) {
            return item.checked;
        }).map(function (item) {
            return { locationBusinessAreaId: item.id, businessArea: item.name };
        }).value();

        var updateSettings = HelperFunctions.deepClone(generalSettings);
        updateSettings.generalSettings.businessAreas= selectedItems;
        onSettingsUpdate(updateSettings);
    }

    function onWorkingTimeValidationChange(isValid) {
        setWorkingTimeValid(isValid);
    }

    function onWorkingTimeSettingsChange(item) {
        var updateSettings = HelperFunctions.deepClone(generalSettings);
        updateSettings.tachoSettings = item;
        onSettingsUpdate(updateSettings);
    }

    function onUseDefaultsChange(item) {
        var updateSettings = HelperFunctions.deepClone(generalSettings);
        updateSettings.tachoSettings.useTachoAnalysisDefaults = item.currentTarget.value === "true";
        onSettingsUpdate(updateSettings);
    }

    function onTachoAnalysisStartDateChange(item) {
        var updateSettings = HelperFunctions.deepClone(generalSettings);
        updateSettings.tachoSettings.tachoAnalysisStartDate = item ? moment(item).format("YYYY-MM-DD") : null;
        onSettingsUpdate(updateSettings);
    }


    return (
        <div>
            <FormTextInput
                id="location-name"
                loading={loadingValue}
                value={generalSettings.generalSettings.name}
                headingText="Enter the location name:"
                onChange={onNameChange}
                disabled={saving}
                dangerHelperText={generalSettingsErrors.name}>
            </FormTextInput>
            <FormTextInput
                id="location-reference"
                loading={loadingValue}
                value={generalSettings.generalSettings.reference}
                headingText="External Reference:"
                onChange={onReferenceChange}
                disabled={saving}
            >
            </FormTextInput>
            <UserAccess perform="Internal"
                yes={() => (

                    <FormCheckboxList
                        id="location-business-areas"
                        items={businessAreas}
                        onChange={handleCheckboxChange}
                        headingText="Business Areas:"
                        loading={loadingValue}
                        dangerHelperText={generalSettingsErrors.businessAreas}
                    />

                )}
            />
            <FormRadio
                id="location-settings-inherited"
                disabled={saving}
                onChange={onUseDefaultsChange}
                headingText="Would you like to inherit the account's tacho settings or apply custom settings?"
                selectedValue={generalSettings.tachoSettings.useTachoAnalysisDefaults}
                options={defaultOptions.inheritSettings}
                loading={loadingValue}
            ></FormRadio>
            <ShowHide
        evaluator={generalSettings.tachoSettings.useTachoAnalysisDefaults === false}
                show={(<Fragment>

                    <FormDateInput
                        id="location-tacho-start"
                        disabled={saving}
                        headingText="The date you wish Vision to analyse your tacho data from:"
                        onChange={onTachoAnalysisStartDateChange}
                        value={generalSettings.tachoSettings.tachoAnalysisStartDate}
                        loading={loadingValue}
                        dangerHelperText={tachoSettingsErrors.tachoAnalysisStartDate}
                        minDate={moment("2000-01-01").toDate()}
                    ></FormDateInput>
                    <WorkingTimeSettings
                        saving={saving}
                        settings={generalSettings.tachoSettings}
                        workingtimeDisabled={!generalSettings.accountTachoSettings.includeWorkingTime}
                        onSettingsUpdate={onWorkingTimeSettingsChange}
                        onValidationUpdate={onWorkingTimeValidationChange}
                    />
                </Fragment>)}
            />
        </div>
    );
}

LocationSettings.propTypes = {
    generalSettings: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    saving: PropTypes.bool,
    editing: PropTypes.bool,
    loading: PropTypes.bool.isRequired
};